import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Play, Square, Coffee, CheckCheck, AlertTriangle, ArrowLeft, History, Lock, Hourglass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  PageHeader, PANEL_CLS, PRIMARY_BTN_CLS, SECONDARY_BTN_CLS,
} from "@/components/premium";

// ---- formatting helpers ---------------------------------------------------
const minToHHMM = (m) => `${Math.floor((m || 0) / 60)}t ${(m || 0) % 60}m`;
const isoToLocal = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("nb-NO", {
    timeZone: "Europe/Oslo", weekday: "short", day: "2-digit", month: "short",
    hour: "2-digit", minute: "2-digit",
  });
};
const isoToTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("nb-NO", {
    timeZone: "Europe/Oslo", hour: "2-digit", minute: "2-digit",
  });
};

const STATUS_LABEL = {
  draft: "Utkast",
  submitted: "Innsendt",
  approved: "Godkjent",
  // Internal status stays `rejected` for architecture / audit reasons;
  // the user-facing wording is the friendlier "Sendt tilbake".
  rejected: "Sendt tilbake",
};
const STATUS_PILL = {
  draft: "bg-muted text-muted-foreground border-border",
  submitted: "bg-amber-500/10 text-amber-600 dark:text-amber-300 border-amber-500/30",
  approved: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 border-emerald-500/30",
  rejected: "bg-rose-500/10 text-rose-600 dark:text-rose-300 border-rose-500/30",
};
const WORK_TYPE_LABEL = {
  customer: "Kundearbeid", admin: "Admin", cleaning: "Rengjøring",
  course: "Kurs", absence: "Fravær", overtime: "Overtid",
};
const ABSENCE_LABEL = {
  sykdom_egen: "Sykdom – egen", sykdom_barn: "Sykdom – barn",
  ferie: "Ferie", permisjon: "Permisjon", annet: "Annet",
};

// Overtime categories — split between employee-selectable and leader-only.
// Employees may only suggest one of the three standard rates or flag
// "unsure". Only the leader can settle on `ot_custom`, and only then is
// a reason mandatory.
const OT_EMP_OPTIONS = [
  { v: "ot40", l: "Overtid 40 %" },
  { v: "ot50", l: "Overtid 50 %" },
  { v: "ot100", l: "Overtid 100 %" },
  { v: "unsure", l: "Usikker – må avklares med leder" },
];
const OT_LEDER_OPTIONS = [
  { v: "ot40", l: "Overtid 40 %" },
  { v: "ot50", l: "Overtid 50 %" },
  { v: "ot100", l: "Overtid 100 %" },
  { v: "ot_custom", l: "Annen avtalt sats" },
];
const OT_LABEL = Object.fromEntries([...OT_EMP_OPTIONS, ...OT_LEDER_OPTIONS].map((o) => [o.v, o.l]));

// ---- Page -----------------------------------------------------------------
export default function Timelister() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // ALWAYS default to the employee/"Min tid" tab — even for users with
  // leader access. Auto-flipping to "Godkjenning" surprised leaders who
  // wanted to log their own time first, and it hid the personal punch
  // card behind a click. Pending approvals are surfaced via a count
  // badge on the Godkjenning tab instead, so urgency is still visible.
  const [tab, setTab] = useState("employee");
  const [confirmStopOpen, setConfirmStopOpen] = useState(false);
  const [activeBreakStart, setActiveBreakStart] = useState(null);
  const [openTsId, setOpenTsId] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);

  const refresh = async () => {
    try {
      const res = await api.get("/timelister/me");
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 402) setData({ blocked: true });
      else toast.error("Kunne ikke laste timelisten.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  // Lightweight pending-approvals count fetch for the Godkjenning tab
  // badge. Only leaders have access — for staff the endpoint 403s and
  // we silently keep the badge at 0. Refreshed alongside refresh() and
  // whenever the LederView calls back after approve/reject.
  const refreshPendingCount = useCallback(async () => {
    if (user?.role !== "owner" && user?.role !== "leder") return;
    try {
      const r = await api.get("/timelister/approvals");
      const items = r.data?.items || [];
      setPendingCount(items.filter((it) => it.status === "submitted").length);
    } catch (_) { /* non-critical */ }
  }, [user?.role]);
  useEffect(() => { refreshPendingCount(); }, [refreshPendingCount]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="h-32 rounded-2xl bg-accent/30 animate-pulse" />
      </div>
    );
  }
  if (data?.blocked) return <PremiumGate />;
  if (!data) return null;

  const isDemo = !!data.demo;
  const isLeder = user?.role === "owner" || user?.role === "leder";

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-32">
      <Helmet><title>Timelister · ClientFlow</title></Helmet>
      <PageHeader eyebrow="Tid & lønn" title="Timelister" />

      {isDemo && <DemoBanner />}

      {isLeder && (
        <div className="mb-5 inline-flex rounded-2xl border border-border bg-card p-1 gap-1" data-testid="timelister-tabs">
          <button
            onClick={() => setTab("employee")}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition ${tab === "employee" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="tab-employee"
          >
            Min tid
          </button>
          <button
            onClick={() => setTab("leder")}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition inline-flex items-center gap-2 ${tab === "leder" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="tab-leder"
          >
            Godkjenning
            {pendingCount > 0 && (
              <span
                className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold inline-flex items-center justify-center tabular-nums ${
                  tab === "leder"
                    ? "bg-primary-foreground/20 text-primary-foreground"
                    : "bg-amber-500/20 text-amber-600 dark:text-amber-300 ring-1 ring-amber-500/30"
                }`}
                data-testid="tab-leder-badge"
              >
                {pendingCount}
              </span>
            )}
          </button>
        </div>
      )}

      {tab === "employee" && (
        <EmployeeView
          data={data}
          isDemo={isDemo}
          onRefresh={refresh}
          confirmStopOpen={confirmStopOpen}
          setConfirmStopOpen={setConfirmStopOpen}
          activeBreakStart={activeBreakStart}
          setActiveBreakStart={setActiveBreakStart}
        />
      )}
      {tab === "leder" && (
        <LederView isDemo={isDemo} openTsId={openTsId} setOpenTsId={setOpenTsId} onRefresh={() => { refresh(); refreshPendingCount(); }} />
      )}
    </div>
  );
}

// ---- Premium gate ---------------------------------------------------------
function PremiumGate() {
  return (
    <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="timelister-premium-gate">
      <Helmet><title>Timelister · ClientFlow</title></Helmet>
      <div className={`${PANEL_CLS} p-8`}>
        <Lock className="w-10 h-10 mx-auto text-emerald-500 mb-4" strokeWidth={1.5} />
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-2">
          Timelister er en del av Premium
        </h1>
        <p className="text-muted-foreground mb-6">
          Profesjonell tidsregistrering med ledergodkjenning og full historikk
          er inkludert i Premium-pakken.
        </p>
        <Link to="/innstillinger">
          <Button className={PRIMARY_BTN_CLS}>Se planene</Button>
        </Link>
      </div>
    </div>
  );
}

// ---- Demo banner ----------------------------------------------------------
function DemoBanner() {
  return (
    <div
      className="mb-5 flex items-start gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.05] p-4"
      data-testid="timelister-demo-banner"
    >
      <Hourglass className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" strokeWidth={1.5} />
      <div className="text-sm">
        <div className="font-semibold">Demo-modus — kun visning</div>
        <div className="text-muted-foreground">
          Du ser eksempeldata for Nordlys Frisør. Knapper er deaktivert.
          Opprett en Premium-konto for å bruke Timelister på egen bedrift.
        </div>
      </div>
    </div>
  );
}

// ---- Employee view --------------------------------------------------------
function EmployeeView({ data, isDemo, onRefresh, confirmStopOpen, setConfirmStopOpen, activeBreakStart, setActiveBreakStart }) {
  const { running_entry: running, active_break: activeBreak, entries, breaks, totals, timesheet } = data;
  const status = timesheet?.status || "draft";
  const submittable = status === "draft" || status === "rejected";

  const start = async () => {
    if (isDemo) return;
    try {
      await api.post("/timelister/workday/start");
      toast.success("Arbeidsdag startet");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunne ikke starte."); }
  };

  const stop = async (confirm = false) => {
    if (isDemo) return;
    try {
      await api.post("/timelister/workday/stop", { confirm_close_break: confirm });
      toast.success("Arbeidsdag stoppet");
      setConfirmStopOpen(false);
      setActiveBreakStart(null);
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "active_break") {
        setActiveBreakStart(detail.break_started_at);
        setConfirmStopOpen(true);
      } else {
        toast.error(typeof detail === "string" ? detail : "Kunne ikke stoppe.");
      }
    }
  };

  const startBreak = async () => {
    if (isDemo) return;
    try {
      await api.post("/timelister/break/start");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunne ikke starte pause."); }
  };

  const endBreak = async () => {
    if (isDemo) return;
    try {
      await api.post("/timelister/break/end");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunne ikke avslutte pause."); }
  };

  const submit = async () => {
    if (isDemo || !timesheet) return;
    try {
      await api.post(`/timelister/timesheet/${timesheet.id}/submit`);
      toast.success("Sendt til godkjenning");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunne ikke sende."); }
  };

  // Employee-side withdrawal: only valid while status === 'submitted'.
  // Re-opens the timesheet to `draft`, removes it from the leader's
  // inbox, and writes an append-only `timesheet.submission_withdrawn`
  // audit row. Confirmed via window.confirm — simpler than a Dialog
  // because there are no extra fields to gather.
  const [withdrawing, setWithdrawing] = useState(false);
  const withdraw = async () => {
    if (isDemo || !timesheet) return;
    if (!window.confirm("Trekke tilbake innsendingen? Du kan da redigere timene og sende inn på nytt.")) return;
    setWithdrawing(true);
    try {
      await api.post(`/timelister/timesheet/${timesheet.id}/withdraw`);
      toast.success("Innsending trukket tilbake");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunne ikke trekke tilbake."); }
    finally { setWithdrawing(false); }
  };

  return (
    <div className="space-y-5">
      {/* Punch card */}
      <div className={`${PANEL_CLS} p-5 sm:p-6`} data-testid="punch-card">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-emerald-500">Arbeidsdag</p>
            <p className="font-heading text-lg mt-0.5">
              {running ? (activeBreak ? "Pause pågår" : "Pågår") : "Ikke startet"}
            </p>
            {running && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Startet {isoToTime(running.start_at)}
              </p>
            )}
          </div>
          <span className={`text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full border ${STATUS_PILL[status]}`}>
            {STATUS_LABEL[status]}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {!running ? (
            <Button onClick={start} disabled={isDemo || !submittable} className={PRIMARY_BTN_CLS} data-testid="btn-start-workday">
              <Play className="w-4 h-4" /> Start arbeidsdag
            </Button>
          ) : (
            <>
              <Button onClick={() => stop(false)} disabled={isDemo} variant="outline" className={SECONDARY_BTN_CLS} data-testid="btn-stop-workday">
                <Square className="w-4 h-4" /> Stopp arbeidsdag
              </Button>
              {!activeBreak ? (
                <Button onClick={startBreak} disabled={isDemo} variant="outline" className={SECONDARY_BTN_CLS} data-testid="btn-start-break">
                  <Coffee className="w-4 h-4" /> Start pause
                </Button>
              ) : (
                <Button onClick={endBreak} disabled={isDemo} variant="outline" className={SECONDARY_BTN_CLS} data-testid="btn-end-break">
                  <Coffee className="w-4 h-4" /> Avslutt pause
                </Button>
              )}
            </>
          )}
        </div>
        {status === "submitted" && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="text-xs text-amber-600 dark:text-amber-300 flex items-center gap-1.5">
              <Hourglass className="w-3.5 h-3.5" /> Innsendt — vent på lederens vurdering.
            </span>
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl"
              onClick={withdraw}
              disabled={isDemo || withdrawing}
              data-testid="btn-withdraw-submission"
            >
              Trekk tilbake innsending
            </Button>
          </div>
        )}
        {status === "rejected" && timesheet?.rejection_note && (
          <div className="mt-4 rounded-xl border border-rose-500/30 bg-rose-500/[0.05] p-3 text-xs text-rose-700 dark:text-rose-200">
            <span className="font-semibold">Sendt tilbake: </span>{timesheet.rejection_note}
          </div>
        )}
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiTile label="Arbeidet" value={minToHHMM(totals.worked_min)} testid="kpi-worked" />
        <KpiTile label="Overtid" value={minToHHMM(totals.overtime_min)} testid="kpi-overtime" />
        <KpiTile label="Fravær" value={minToHHMM(totals.absence_min)} testid="kpi-absence" />
        <KpiTile label="Pauser" value={minToHHMM(totals.break_min)} testid="kpi-breaks" />
      </div>

      {/* Add manual entry */}
      {!isDemo && submittable && <ManualEntryForm onSaved={onRefresh} />}

      {/* Entries list */}
      <div>
        <h2 className="font-heading text-lg font-semibold mb-3">Mine timer</h2>
        {entries.length === 0 ? (
          <div className={`${PANEL_CLS} p-8 text-center text-sm text-muted-foreground`} data-testid="entries-empty">
            Ingen timer ennå denne måneden. Trykk «Start arbeidsdag» for å begynne.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="entries-list">
            {entries.map((e) => <EntryRow key={e.id} entry={e} breaks={breaks} canEdit={!isDemo && submittable} onRefresh={onRefresh} />)}
          </ul>
        )}
      </div>

      {/* Submit CTA */}
      {timesheet && submittable && entries.length > 0 && (
        <div className="sticky bottom-4 left-0 right-0 z-10">
          <Button onClick={submit} disabled={isDemo} className={`${PRIMARY_BTN_CLS} w-full`} data-testid="btn-submit-timesheet">
            <CheckCheck className="w-4 h-4" /> Send timeliste til godkjenning
          </Button>
        </div>
      )}

      {/* Confirm-close-active-break dialog */}
      <Dialog open={confirmStopOpen} onOpenChange={setConfirmStopOpen}>
        <DialogContent data-testid="confirm-stop-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Du har en pågående pause
            </DialogTitle>
            <DialogDescription>
              Stopper du arbeidsdagen nå, avsluttes pausen på samme tidspunkt
              {activeBreakStart && <> (startet {isoToTime(activeBreakStart)})</>}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className={SECONDARY_BTN_CLS} onClick={() => setConfirmStopOpen(false)} data-testid="confirm-stop-cancel">Avbryt</Button>
            <Button className={PRIMARY_BTN_CLS} onClick={() => stop(true)} data-testid="confirm-stop-ok">Ja, stopp begge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiTile({ label, value, testid }) {
  return (
    <div className={`${PANEL_CLS} p-4`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">{label}</div>
      <div className="font-heading text-xl font-bold mt-1">{value}</div>
    </div>
  );
}

function EntryRow({ entry, breaks, canEdit, onRefresh }) {
  const myBreaks = breaks.filter((b) => b.time_entry_id === entry.id);
  const del = async () => {
    if (!window.confirm("Slett denne tidsoppføringen?")) return;
    try {
      await api.delete(`/timelister/entries/${entry.id}`);
      toast.success("Slettet");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunne ikke slette."); }
  };
  return (
    <li className={`${PANEL_CLS} p-4 flex items-start justify-between gap-3`} data-testid={`entry-${entry.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold">{isoToLocal(entry.start_at)}</span>
          <span className="text-muted-foreground">→ {isoToTime(entry.end_at)}</span>
          <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/[0.05] text-emerald-600 dark:text-emerald-300">
            {entry.work_type === "absence" && entry.absence_kind ? ABSENCE_LABEL[entry.absence_kind] : WORK_TYPE_LABEL[entry.work_type]}
          </span>
          {entry.work_type === "overtime" && entry.overtime_category_employee && (
            <span
              className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border border-amber-500/30 bg-amber-500/[0.05] text-amber-600 dark:text-amber-300"
              data-testid={`ot-emp-${entry.id}`}
              title="Ansattes forslag"
            >
              {OT_LABEL[entry.overtime_category_employee] || entry.overtime_category_employee}
            </span>
          )}
          {entry.work_type === "overtime" && entry.overtime_category_leder && (
            <span
              className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border border-sky-500/30 bg-sky-500/[0.05] text-sky-600 dark:text-sky-300"
              data-testid={`ot-leder-${entry.id}`}
              title="Lederens beslutning"
            >
              Leder: {OT_LABEL[entry.overtime_category_leder] || entry.overtime_category_leder}
            </span>
          )}
        </div>
        {myBreaks.length > 0 && (
          <div className="text-xs text-muted-foreground mt-1">
            {myBreaks.length} pause{myBreaks.length > 1 ? "r" : ""}
          </div>
        )}
        {entry.note && <div className="text-xs text-muted-foreground mt-1">«{entry.note}»</div>}
      </div>
      {canEdit && (
        <Button variant="ghost" size="sm" onClick={del} className="text-rose-600 hover:bg-rose-500/10" data-testid={`entry-delete-${entry.id}`}>
          Slett
        </Button>
      )}
    </li>
  );
}

function ManualEntryForm({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    start: "08:00", end: "16:00",
    work_type: "customer", absence_kind: "ferie",
    overtime_category_employee: "unsure",
    note: "",
  });
  const [saving, setSaving] = useState(false);
  const isSickness = form.work_type === "absence" && (form.absence_kind === "sykdom_egen" || form.absence_kind === "sykdom_barn");
  const isOvertime = form.work_type === "overtime";

  const save = async () => {
    setSaving(true);
    try {
      // Convert local Oslo wall-clock → UTC ISO. We build a local date
      // string and let the browser interpret it; this is approximate
      // since the browser may be in a different tz, but the backend
      // also recomputes local_date_start from the UTC instant.
      const localDate = form.date;
      const start_at = new Date(`${localDate}T${form.start}:00`).toISOString();
      const end_at = new Date(`${localDate}T${form.end}:00`).toISOString();
      const payload = {
        date: localDate, start_at, end_at, work_type: form.work_type, note: form.note || null,
      };
      if (form.work_type === "absence") {
        payload.absence_kind = form.absence_kind;
        if (!isSickness && form.note) payload.absence_note = form.note;
      }
      if (isOvertime) {
        payload.overtime_category_employee = form.overtime_category_employee;
      }
      await api.post("/timelister/entries", payload);
      toast.success("Tid registrert");
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunne ikke lagre."); }
    finally { setSaving(false); }
  };

  if (!open) {
    return (
      <Button variant="outline" className={SECONDARY_BTN_CLS} onClick={() => setOpen(true)} data-testid="btn-manual-entry-open">
        Legg til timer manuelt
      </Button>
    );
  }
  return (
    <div className={`${PANEL_CLS} p-4 space-y-3`} data-testid="manual-entry-form">
      <div className="grid grid-cols-3 gap-2">
        <div><Label className="text-xs">Dato</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="manual-date" /></div>
        <div><Label className="text-xs">Fra</Label><Input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="manual-start" /></div>
        <div><Label className="text-xs">Til</Label><Input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="manual-end" /></div>
      </div>
      <div>
        <Label className="text-xs">Type</Label>
        <select className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm" value={form.work_type} onChange={(e) => setForm({ ...form, work_type: e.target.value })} data-testid="manual-worktype">
          {Object.entries(WORK_TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      {form.work_type === "absence" && (
        <div>
          <Label className="text-xs">Fraværstype</Label>
          <select className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm" value={form.absence_kind} onChange={(e) => setForm({ ...form, absence_kind: e.target.value })} data-testid="manual-absence">
            {Object.entries(ABSENCE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {isSickness && (
            <p className="text-[11px] text-muted-foreground mt-1">
              Detaljer om sykdom skal ikke registreres her. Kun fraværstype.
            </p>
          )}
        </div>
      )}
      {isOvertime && (
        <div>
          <Label className="text-xs">Overtidskategori</Label>
          <select
            className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm"
            value={form.overtime_category_employee}
            onChange={(e) => setForm({ ...form, overtime_category_employee: e.target.value })}
            data-testid="manual-overtime-category"
          >
            {OT_EMP_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
          <p className="text-[11px] text-muted-foreground mt-1">
            Lederen bekrefter eller justerer kategorien før godkjenning.
          </p>
        </div>
      )}
      <div>
        <Label className="text-xs">Notat (valgfritt)</Label>
        <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} maxLength={140} disabled={isSickness} data-testid="manual-note" />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" className={SECONDARY_BTN_CLS} onClick={() => setOpen(false)} data-testid="manual-cancel">Avbryt</Button>
        <Button className={PRIMARY_BTN_CLS} onClick={save} disabled={saving} data-testid="manual-save">Lagre</Button>
      </div>
    </div>
  );
}

// ---- Leder view -----------------------------------------------------------
function LederView({ isDemo, openTsId, setOpenTsId, onRefresh }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("submitted");

  const load = async () => {
    try {
      const r = await api.get("/timelister/approvals");
      setItems(r.data?.items || []);
    } catch (err) {
      if (err.response?.status !== 403) toast.error("Kunne ikke laste godkjenninger.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => items.filter((i) => filter === "all" || i.status === filter),
    [items, filter]
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" data-testid="leder-filters">
        {[
          { v: "submitted", label: "Til godkjenning" },
          { v: "approved", label: "Godkjente" },
          { v: "rejected", label: "Sendt tilbake" },
          { v: "all", label: "Historikk" },
        ].map((f) => (
          <button
            key={f.v}
            onClick={() => setFilter(f.v)}
            className={`px-3 py-1.5 rounded-full text-sm border transition ${filter === f.v ? "border-emerald-500/40 bg-emerald-500/[0.08] text-foreground" : "border-border text-muted-foreground hover:border-emerald-500/30"}`}
            data-testid={`filter-${f.v}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="h-24 rounded-2xl bg-accent/30 animate-pulse" />
      ) : filtered.length === 0 ? (
        <div className={`${PANEL_CLS} p-8 text-center text-sm text-muted-foreground`} data-testid="approvals-empty">
          Ingen timelister i denne kategorien.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="approvals-list">
          {filtered.map((it) => (
            <li key={it.id} className={`${PANEL_CLS} p-4 flex items-center justify-between gap-3 cursor-pointer hover:border-emerald-500/30 transition`}
                onClick={() => setOpenTsId(it.id)} data-testid={`approval-${it.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{it.user_name || "Ansatt"}</div>
                <div className="text-xs text-muted-foreground">{it.period_start} → {it.period_end}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full border ${STATUS_PILL[it.status]}`}>
                {STATUS_LABEL[it.status]}
              </span>
            </li>
          ))}
        </ul>
      )}
      {openTsId && (
        <TimesheetReviewDialog
          tsId={openTsId}
          isDemo={isDemo}
          onClose={() => { setOpenTsId(null); load(); onRefresh(); }}
        />
      )}
    </div>
  );
}

function LederOvertimeEditor({ entry, onSaved, isDemo }) {
  const initial = entry.overtime_category_leder || entry.overtime_category_employee || "ot50";
  const [category, setCategory] = useState(initial);
  const [reason, setReason] = useState(entry.overtime_category_leder_reason || "");
  const [saving, setSaving] = useState(false);
  const needsReason = category === "ot_custom";
  const dirty = category !== (entry.overtime_category_leder || null) || (needsReason && reason !== (entry.overtime_category_leder_reason || ""));

  const save = async () => {
    if (needsReason && reason.trim().length < 4) {
      toast.error("«Annen avtalt sats» krever en kort begrunnelse.");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/timelister/entries/${entry.id}/overtime-category`, {
        category,
        reason: needsReason ? reason.trim() : null,
      });
      toast.success("Overtidskategori oppdatert");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke lagre.");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="ml-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.04] p-3 text-sm space-y-2"
      data-testid={`ot-editor-${entry.id}`}
    >
      <div className="text-[11px] uppercase tracking-widest font-semibold text-amber-600 dark:text-amber-300">
        Overtidskategori – leder bekrefter
      </div>
      <div className="text-xs text-muted-foreground">
        Ansattes forslag: <span className="font-medium">{OT_LABEL[entry.overtime_category_employee] || "—"}</span>
      </div>
      <select
        className="w-full rounded-xl border border-border bg-card px-3 py-2 text-sm"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        data-testid={`ot-leder-select-${entry.id}`}
      >
        {OT_LEDER_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
      {needsReason && (
        <Input
          placeholder="Begrunnelse for annen sats (kreves)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={140}
          data-testid={`ot-leder-reason-${entry.id}`}
        />
      )}
      <div className="flex justify-end">
        <Button
          size="sm"
          className={PRIMARY_BTN_CLS}
          onClick={save}
          disabled={isDemo || saving || !dirty}
          data-testid={`ot-leder-save-${entry.id}`}
        >
          Lagre kategori
        </Button>
      </div>
    </div>
  );
}

function TimesheetReviewDialog({ tsId, isDemo, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/timelister/timesheet/${tsId}`);
      setData(r.data);
    } catch { toast.error("Kunne ikke laste timelisten."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tsId]);

  const approve = async () => {
    try {
      await api.post(`/timelister/timesheet/${tsId}/approve`);
      toast.success("Godkjent");
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunne ikke godkjenne."); }
  };
  const reject = async () => {
    if (reason.trim().length < 4) { toast.error("Skriv en kort begrunnelse."); return; }
    try {
      await api.post(`/timelister/timesheet/${tsId}/reject`, { reason });
      toast.success("Sendt tilbake til ansatt");
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunne ikke sende tilbake."); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="timesheet-review-dialog">
        <DialogHeader>
          <DialogTitle>{data?.user?.name || "Timeliste"}</DialogTitle>
          <DialogDescription>
            {data ? `${data.timesheet.period_start} → ${data.timesheet.period_end}` : "Laster…"}
          </DialogDescription>
        </DialogHeader>
        {loading || !data ? (
          <div className="h-24 rounded-xl bg-accent/30 animate-pulse" />
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <KpiTile label="Arbeidet" value={minToHHMM(data.totals.worked_min)} testid="r-kpi-worked" />
              <KpiTile label="Overtid" value={minToHHMM(data.totals.overtime_min)} testid="r-kpi-ot" />
              <KpiTile label="Fravær" value={minToHHMM(data.totals.absence_min)} testid="r-kpi-abs" />
              <KpiTile label="Pauser" value={minToHHMM(data.totals.break_min)} testid="r-kpi-br" />
            </div>
            <ul className="space-y-2">
              {data.entries.map((e) => (
                <div key={e.id} className="space-y-1.5">
                  <EntryRow entry={e} breaks={data.breaks} canEdit={false} onRefresh={() => {}} />
                  {e.work_type === "overtime" && data.timesheet.status === "submitted" && (
                    <LederOvertimeEditor entry={e} onSaved={load} isDemo={isDemo} />
                  )}
                </div>
              ))}
              {data.entries.length === 0 && (
                <li className="text-sm text-muted-foreground italic">Ingen oppføringer.</li>
              )}
            </ul>
            <details className="rounded-xl border border-border p-3 text-sm">
              <summary className="cursor-pointer text-muted-foreground flex items-center gap-1.5"><History className="w-4 h-4" /> Historikk ({data.audit.length})</summary>
              <ul className="mt-3 space-y-1.5 text-xs" data-testid="audit-list">
                {data.audit.map((a) => (
                  <li key={a.id} className="border-l-2 border-emerald-500/30 pl-3">
                    <div className="font-medium">{a.action}</div>
                    <div className="text-muted-foreground">{isoToLocal(a.occurred_at)} · {a.actor_role}</div>
                    {a.reason && <div className="text-rose-600 dark:text-rose-300">«{a.reason}»</div>}
                  </li>
                ))}
                {data.audit.length === 0 && <li className="text-muted-foreground italic">Ingen historikk.</li>}
              </ul>
            </details>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className={SECONDARY_BTN_CLS} onClick={onClose}>Lukk</Button>
          {data?.timesheet.status === "submitted" && (
            <>
              <Button variant="outline" className={`${SECONDARY_BTN_CLS} text-rose-600`} onClick={() => setRejectOpen(true)} disabled={isDemo} data-testid="btn-reject">
                Send tilbake for endring
              </Button>
              <Button className={PRIMARY_BTN_CLS} onClick={approve} disabled={isDemo} data-testid="btn-approve">
                <CheckCheck className="w-4 h-4" /> Godkjenn
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Send tilbake for endring</DialogTitle>
            <DialogDescription>Begrunn kort hva som må endres — den ansatte ser dette i historikken.</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="F.eks. Dobbelregistrert torsdag" data-testid="reject-reason" />
          <DialogFooter>
            <Button variant="outline" className={SECONDARY_BTN_CLS} onClick={() => setRejectOpen(false)}>Avbryt</Button>
            <Button className="rounded-xl bg-rose-600 hover:bg-rose-700 text-white" onClick={reject} data-testid="btn-reject-confirm">Send tilbake</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
